<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  parseDetailReportFilters,
  generateReportURLParams,
  generateSummaryEntityFilterParams,
} from './helpers/reportFilterHelper';
import ReportFiltersSection from './components/ReportFiltersSection.vue';
import SummaryDetailHeader from './components/SummaryDetailHeader.vue';
import SummaryDetailMetrics from './components/SummaryDetailMetrics.vue';
import SummaryEntityDetailCharts from './components/SummaryEntityDetailCharts.vue';
import SummaryDetailHeatmaps from './components/SummaryDetailHeatmaps.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const team = useFunctionGetter('teams/getTeamById', route.params.id);

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedInboxIds = ref([]);
const selectedAgentIds = ref([]);
const selectedLabelIds = ref([]);
const excludeLabelIds = ref([]);
const receiptType = ref('all');

const applyEntityFilters = filters => {
  selectedInboxIds.value = filters.inboxIds || [];
  selectedAgentIds.value = filters.agentIds || [];
  selectedLabelIds.value = filters.labelIds || [];
  excludeLabelIds.value = filters.excludeLabelIds || [];
  receiptType.value = filters.receiptType || 'all';
};

const initializeFromURL = () => {
  const parsed = parseDetailReportFilters(route.query);
  from.value = parsed.from || 0;
  to.value = parsed.to || 0;
  businessHours.value = parsed.businessHours;
  applyEntityFilters(parsed);
};

initializeFromURL();

const detailFilters = computed(() => ({
  from: from.value,
  to: to.value,
  businessHours: businessHours.value,
  inboxIds: selectedInboxIds.value,
  agentIds: selectedAgentIds.value,
  labelIds: selectedLabelIds.value,
  excludeLabelIds: excludeLabelIds.value,
  teamIds: [Number(route.params.id)],
  receiptType: receiptType.value,
}));

const updateURLParams = () => {
  const entityParams = generateSummaryEntityFilterParams({
    inboxIds: selectedInboxIds.value,
    agentIds: selectedAgentIds.value,
    labelIds: selectedLabelIds.value,
    excludeLabelIds: excludeLabelIds.value,
    receiptType: receiptType.value,
  });
  const dateParams = generateReportURLParams({
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
  });
  router.replace({ query: { ...dateParams, ...entityParams } });
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  businessHours.value = date.businessHours;
  applyEntityFilters(entity);
  updateURLParams();
};

onMounted(() => store.dispatch('teams/get'));
watch(() => route.query, initializeFromURL);
</script>

<template>
  <div v-if="team.id" class="flex flex-col gap-4">
    <SummaryDetailHeader
      :title="team.name"
      report-type="team"
      :filters="detailFilters"
      :download-label="$t('TEAM_REPORTS.DOWNLOAD_TEAM_REPORTS')"
    />
    <ReportFiltersSection
      type="team"
      hide-team-filter
      @filters-apply="onFiltersApply"
    />
    <SummaryDetailMetrics
      type="team"
      :entity-id="team.id"
      :filters="detailFilters"
    />
    <SummaryEntityDetailCharts
      type="team"
      :entity-id="team.id"
      :filters="detailFilters"
    />
    <SummaryDetailHeatmaps
      entity-type="team"
      :entity-id="team.id"
      :filters="detailFilters"
    />
  </div>
  <div v-else class="w-full py-20">
    <Spinner class="mx-auto" />
  </div>
</template>
